"use client";
import { Card } from "@workspace/ui/components/card";
import {
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from "@workspace/ui/components/tabs";
import { useSearchParams } from "next/navigation";
import {
	lazy,
	Suspense,
	startTransition,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from "react";
import { ShowGrid } from "@/components/features/show/discovery/show-grid";
import { ShowCityFilter } from "@/components/features/show/filters/show-city-filter";
import { ShowGenreFilter } from "@/components/features/show/filters/show-genre-filter";
import { ShowDateFilter, type DateFilterValue, type DatePreset } from "@/components/features/show/filters/show-date-filter";
import { ShowVenueFilter } from "@/components/features/show/filters/show-venue-filter";
import { ShowDiscoverySkeleton } from "@/components/features/show/skeletons";
import {
	SearchEmptyState,
	ShowsEmptyState,
} from "@/components/features/show/states";
import { EnhancedSearchInput } from "@/components/shared/filters/search/enhanced-search-input";
import { SocialFilter } from "@/components/shared/filters/social/social-filter";
import { Spinner } from "@workspace/ui/components/spinner";
import {
	useShowFiltersData,
	useShowsInfinite,
} from "@/hooks/features/show/data/migration";
import { useShowFacepile } from "@/hooks/features/show/use-show-facepile";
import { useShowSocialInteractionsList } from "@/hooks/features/show/data/trpc";
import { useSocialFilter } from "@/hooks/features/social";
import { useSearchInput } from "@/hooks/shared";
import { useResponsiveLimit } from "@/hooks/shared/use-responsive-limit";
import { useUserPreferences } from "@/hooks/shared/use-user-preferences";
import { useInfiniteScroll } from "@/hooks/shared/use-infinite-scroll";
import { useSupabase } from "@/components/providers/supabase-auth-provider";
import { cn } from "@/lib/utils";
import type { ShowFacepileData } from "@/server/api/routers/show/discovery";

// Simple loading component for lazy tabs
function TabLoadingSkeleton() {
	const { initialLimit } = useResponsiveLimit();
	return (
		<div className="space-y-3">
			{[...Array(initialLimit)].map((_, i) => (
				<ShowDiscoverySkeleton key={`skeleton-${i}`} />
			))}
		</div>
	);
}

// Lazy load the ShowTabContent for non-active tabs
const LazyShowTabContent = lazy(() =>
	Promise.resolve({ default: ShowTabContent }),
);

// Use flexible show type that matches tRPC response
interface Show {
	id: string;
	title: string;
	artist_name: string;
	date: string;
	location: string;
	slug: string;
	created_at: string;
	updated_at: string;
	venue_id: string;
	venue: {
		id: string;
		name: string;
		slug: string;
		city: string;
		country: string;
		[key: string]: unknown;
	};
	[key: string]: unknown; // Allow additional fields from API
}

interface ShowTabContentProps {
	isInitialLoad: boolean;
	showsLoading: boolean;
	data: unknown;
	currentShows: Show[];
	facepileData: ShowFacepileData | undefined;
	isFacepileLoading: boolean;
	isFetchingNextPage: boolean;
	hasActiveFilters: boolean;
	searchTerm: string;
	selectedCity: string;
	selectedDateLabel: string;
	selectedVenue: string;
	selectedGenres: string[];
	onClearSearch: () => void;
	onClearCity: () => void;
	onClearDate: () => void;
	onClearVenue: () => void;
	onClearGenres: () => void;
	variant: "now" | "upcoming" | "past" | "all";
	skeletonCount: number;
}

function ShowTabContent({
	isInitialLoad,
	showsLoading,
	data,
	currentShows,
	facepileData,
	isFacepileLoading,
	isFetchingNextPage,
	hasActiveFilters,
	searchTerm,
	selectedCity,
	selectedDateLabel,
	selectedVenue,
	selectedGenres,
	onClearSearch,
	onClearCity,
	onClearDate,
	onClearVenue,
	onClearGenres,
	variant,
	skeletonCount,
}: ShowTabContentProps) {
	return (
		<div>
			{/* Initial loading state */}
			{((isInitialLoad && showsLoading) || (showsLoading && !data)) && (
				<div className="space-y-3">
					{[...Array(skeletonCount)].map((_, i) => (
						<ShowDiscoverySkeleton key={`loading-${i}`} />
					))}
				</div>
			)}

			{/* Shows grid when we have data */}
			{!((isInitialLoad && showsLoading) || (showsLoading && !data)) &&
				currentShows.length > 0 && (
					<ShowGrid
						shows={currentShows}
						facepileData={facepileData || {}}
						isFacepileLoading={isFacepileLoading}
					/>
				)}

			{/* Empty states */}
			{!((isInitialLoad && showsLoading) || (showsLoading && !data)) &&
				currentShows.length === 0 &&
				(hasActiveFilters ? (
					<SearchEmptyState
						searchTerm={searchTerm}
						selectedCity={selectedCity}
						selectedDateLabel={selectedDateLabel}
						selectedVenue={selectedVenue}
						selectedGenres={selectedGenres}
						onClearSearch={onClearSearch}
						onClearCity={onClearCity}
						onClearDate={onClearDate}
						onClearVenue={onClearVenue}
						onClearGenres={onClearGenres}
					/>
				) : (
					<ShowsEmptyState variant={variant} />
				))}

			{/* Loading indicator for infinite scroll */}
			{isFetchingNextPage && (
				<div className="flex justify-center py-4">
					<Spinner variant="muted" />
				</div>
			)}
		</div>
	);
}

export function ShowsPageClient() {
	// Track initial loading state
	const [isInitialLoad, setIsInitialLoad] = useState(true);

	// Get responsive limits for optimal performance on different screen sizes
	const { initialLimit } = useResponsiveLimit();

	// Get user preferences for default location
	const userPreferences = useUserPreferences();

	// Read URL parameters for filter initialization
	const searchParams = useSearchParams();

	// Use the new search hook
	const search = useSearchInput({
		debounceMs: 300,
	});

	// Local state for tabs (following project standards)
	const [selectedTab, setSelectedTab] = useState<
		"now" | "upcoming" | "past" | "all"
	>("now");
	// Initialize city with "all" and let useEffect set the preference
	const [selectedCity, setSelectedCity] = useState<string>("all");
	const [dateFilter, setDateFilter] = useState<DateFilterValue>({
		preset: null,
		startDate: null,
		endDate: null,
	});
	const [selectedVenue, setSelectedVenue] = useState<string>(
		searchParams?.get("venue") || "all"
	);
	const [selectedGenres, setSelectedGenres] = useState<string[]>([]);

	// Get user for authentication check
	const { user } = useSupabase();

	// Social filter state
	const { isActive: isSocialFilterActive, toggle: toggleSocialFilter } =
		useSocialFilter();

	// Handle social filter toggle
	const handleToggleSocialFilter = useCallback(() => {
		toggleSocialFilter();
	}, [toggleSocialFilter]);

	// Fetch filter options with cascading filter support
	const { data: filterData } = useShowFiltersData({
		city: selectedCity,
		venueId: selectedVenue,
		genres: selectedGenres,
		startDate: dateFilter.startDate,
		endDate: dateFilter.endDate,
	});

	// Use infinite query hook
	const {
		data,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
		isLoading: showsLoading,
		error,
	} = useShowsInfinite({
		search: search.debouncedValue,
		tab: selectedTab,
		city: selectedCity === "all" ? undefined : selectedCity,
		startDate: dateFilter.startDate ?? undefined,
		endDate: dateFilter.endDate ?? undefined,
		venueId: selectedVenue === "all" ? undefined : selectedVenue,
		genres: selectedGenres.length > 0 ? selectedGenres : undefined,
		limit: initialLimit,
	});

	// Flatten all pages into single array
	const shows = useMemo(() => {
		const typedData = data as { pages?: Array<{ items: Show[] }> } | undefined;
		if (!typedData?.pages) return [];
		return typedData.pages.flatMap((page) => page.items);
	}, [data]);

	// Get show IDs for facepile fetching
	const showIds = useMemo(() => {
		return shows.map((s) => s.id).filter(Boolean);
	}, [shows]);

	// Fetch social interactions data when social filter is active
	const { data: socialInteractionsData, isLoading: isSocialDataLoading } =
		useShowSocialInteractionsList(showIds, isSocialFilterActive) as { data: Record<string, number> | undefined; isLoading: boolean };

	// Apply social filter if active AND data has loaded
	const filteredShows = useMemo(() => {
		// Only filter if social filter is active AND we have data (not loading)
		if (isSocialFilterActive && !isSocialDataLoading && socialInteractionsData) {
			return shows.filter((show) => {
				return (socialInteractionsData[show.id] || 0) > 0;
			});
		}
		// If loading or filter is off, show all shows
		return shows;
	}, [shows, isSocialFilterActive, isSocialDataLoading, socialInteractionsData]);

	// Fetch facepile data separately with REALTIME tier
	const { data: facepileDataResponse, isLoading: isFacepileLoading } =
		useShowFacepile(showIds, {
			enabled: showIds.length > 0,
		});

	// Use the facepile data from the separate hook
	const facepileData = facepileDataResponse as ShowFacepileData | undefined;

	// Enable infinite scroll
	useInfiniteScroll({
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
		isActive: true,
		threshold: 800,
	});

	// Set initial load to false when we have data
	// With ISR, data is immediately available, so no skeleton needed
	useEffect(() => {
		// If we already have data (from SSR/ISR), don't show skeleton
		if (data) {
			setIsInitialLoad(false);
			return;
		}
		
		// Only show skeleton if we're actually loading without data
		if (!showsLoading) {
			// Small delay to ensure smooth transition from skeleton to content
			const timer = setTimeout(() => {
				// Use React 18 startTransition for non-urgent update
				startTransition(() => {
					setIsInitialLoad(false);
				});
			}, 100);
			return () => clearTimeout(timer);
		}
	}, [showsLoading, data]);

	// Update city filter when user preference changes
	// BUT only if no venue is specified in URL (venue filter takes precedence)
	useEffect(() => {
		// If venue is specified in URL, don't apply location preference - keep city as "all"
		const venueFromUrl = searchParams?.get("venue");
		if (venueFromUrl) {
			setSelectedCity("all");
		} else {
			setSelectedCity(userPreferences.location_preference || "all");
		}
	}, [userPreferences.location_preference, searchParams]);


	// Check if any filters are active
	// Compute display label for the active date filter
	const dateFilterLabel = useMemo(() => {
		if (dateFilter.preset) {
			const labels: Record<DatePreset, string> = {
				"tonight": "tonight",
				"this-weekend": "this weekend",
				"this-week": "this week",
				"this-month": "this month",
			};
			return labels[dateFilter.preset];
		}
		if (dateFilter.startDate && dateFilter.endDate) {
			if (dateFilter.startDate === dateFilter.endDate) return `on ${dateFilter.startDate}`;
			return `from ${dateFilter.startDate} to ${dateFilter.endDate}`;
		}
		return "";
	}, [dateFilter]);

	const hasActiveFilters =
		!!search.debouncedValue.trim() ||
		selectedCity !== "all" ||
		dateFilter.startDate !== null ||
		selectedVenue !== "all" ||
		selectedGenres.length > 0;

	// Get current shows based on tab (API already filters)
	const currentShows = filteredShows;

	if (error) {
		return (
			<div className="mx-auto flex max-w-3xl flex-1 flex-col gap-3 p-3">
				<Card className="p-8 text-center">
					<h3 className="mb-2 font-semibold text-xl">Error Loading Shows</h3>
					<p className="text-muted-foreground text-sm">
						Failed to load shows. Please try refreshing the page.
					</p>
				</Card>
			</div>
		);
	}

	return (
		<>
			<div className="mx-auto flex max-w-3xl flex-1 flex-col gap-3 p-3">
				<Tabs
					key="shows-tabs"
					value={selectedTab}
					onValueChange={(value) => {
						setSelectedTab(value as "now" | "upcoming" | "past" | "all");
					}}
				>
					<div>
						<TabsList className="grid w-full grid-cols-4">
							<TabsTrigger value="now" className="group relative w-full">
								Now
							</TabsTrigger>
							<TabsTrigger value="upcoming" className="group relative w-full">
								Upcoming
							</TabsTrigger>
							<TabsTrigger value="past" className="group relative w-full">
								Past
							</TabsTrigger>
							<TabsTrigger value="all" className="group relative w-full">
								All
							</TabsTrigger>
						</TabsList>
					</div>

					<div className={cn("sticky top-14 z-30 bg-background py-3")}>
						<div className="space-y-3">
							<div className="grid grid-cols-2 gap-3 md:grid-cols-4">
								<ShowCityFilter
									value={selectedCity}
									onChange={setSelectedCity}
									locationGroups={
										Array.isArray(filterData?.locationGroups) ? filterData.locationGroups : []
									}
								/>
								<ShowVenueFilter
									value={selectedVenue}
									onChange={setSelectedVenue}
									availableVenues={
										Array.isArray(filterData?.venues) ? filterData.venues : []
									}
								/>
								<ShowDateFilter
									startDate={dateFilter.startDate}
									endDate={dateFilter.endDate}
									preset={dateFilter.preset}
									onChange={setDateFilter}
								/>
								<ShowGenreFilter
									value={selectedGenres}
									onChange={setSelectedGenres}
									availableGenres={
										Array.isArray(filterData?.genres) ? filterData.genres : []
									}
								/>
							</div>

							<div className="flex gap-3">
								<EnhancedSearchInput
									value={search.value}
									onValueChange={(newValue) => {
										search.setValue(newValue);
										// Automatically switch to "All" tab when user starts typing
										if (newValue.trim() && selectedTab !== "all") {
											setSelectedTab("all");
										}
									}}
									placeholder="Search shows, venues, cities..."
									showLoadingIndicator={true}
									isLoading={search.isSearching}
									className="flex-1"
								/>
								<SocialFilter
									isActive={isSocialFilterActive}
									onClick={handleToggleSocialFilter}
									disabled={!user}
								/>
							</div>
						</div>
					</div>

					<TabsContent value="now" className="mt-0">
						{selectedTab === "now" ? (
							<ShowTabContent
								isInitialLoad={isInitialLoad}
								showsLoading={showsLoading}
								data={data}
								currentShows={currentShows}
								facepileData={facepileData}
								isFacepileLoading={isFacepileLoading}
								isFetchingNextPage={isFetchingNextPage}
								hasActiveFilters={hasActiveFilters}
								searchTerm={search.value}
								selectedCity={selectedCity}
								selectedDateLabel={dateFilterLabel}
								selectedVenue={selectedVenue}
								selectedGenres={selectedGenres}
								onClearSearch={() => search.setValue("")}
								onClearCity={() => setSelectedCity("all")}
								onClearDate={() => setDateFilter({ preset: null, startDate: null, endDate: null })}
								onClearVenue={() => setSelectedVenue("all")}
								onClearGenres={() => setSelectedGenres([])}
								variant="now"
								skeletonCount={initialLimit}
							/>
						) : (
							<Suspense fallback={<TabLoadingSkeleton />}>
								<LazyShowTabContent
									isInitialLoad={isInitialLoad}
									showsLoading={showsLoading}
									data={data}
									currentShows={currentShows}
									facepileData={facepileData}
									isFacepileLoading={isFacepileLoading}
									isFetchingNextPage={isFetchingNextPage}
									hasActiveFilters={hasActiveFilters}
									searchTerm={search.value}
									selectedCity={selectedCity}
									selectedDateLabel={dateFilterLabel}
									selectedVenue={selectedVenue}
									selectedGenres={selectedGenres}
									onClearSearch={() => search.setValue("")}
									onClearCity={() => setSelectedCity("all")}
									onClearDate={() => setDateFilter({ preset: null, startDate: null, endDate: null })}
									onClearVenue={() => setSelectedVenue("all")}
									onClearGenres={() => setSelectedGenres([])}
									variant="now"
									skeletonCount={initialLimit}
								/>
							</Suspense>
						)}
					</TabsContent>

					<TabsContent value="upcoming" className="mt-0">
						{selectedTab === "upcoming" ? (
							<ShowTabContent
								isInitialLoad={isInitialLoad}
								showsLoading={showsLoading}
								data={data}
								currentShows={currentShows}
								facepileData={facepileData}
								isFacepileLoading={isFacepileLoading}
								isFetchingNextPage={isFetchingNextPage}
								hasActiveFilters={hasActiveFilters}
								searchTerm={search.value}
								selectedCity={selectedCity}
								selectedDateLabel={dateFilterLabel}
								selectedVenue={selectedVenue}
								selectedGenres={selectedGenres}
								onClearSearch={() => search.setValue("")}
								onClearCity={() => setSelectedCity("all")}
								onClearDate={() => setDateFilter({ preset: null, startDate: null, endDate: null })}
								onClearVenue={() => setSelectedVenue("all")}
								onClearGenres={() => setSelectedGenres([])}
								variant="upcoming"
								skeletonCount={initialLimit}
							/>
						) : (
							<Suspense fallback={<TabLoadingSkeleton />}>
								<LazyShowTabContent
									isInitialLoad={isInitialLoad}
									showsLoading={showsLoading}
									data={data}
									currentShows={currentShows}
									facepileData={facepileData}
									isFacepileLoading={isFacepileLoading}
									isFetchingNextPage={isFetchingNextPage}
									hasActiveFilters={hasActiveFilters}
									searchTerm={search.value}
									selectedCity={selectedCity}
									selectedDateLabel={dateFilterLabel}
									selectedVenue={selectedVenue}
									selectedGenres={selectedGenres}
									onClearSearch={() => search.setValue("")}
									onClearCity={() => setSelectedCity("all")}
									onClearDate={() => setDateFilter({ preset: null, startDate: null, endDate: null })}
									onClearVenue={() => setSelectedVenue("all")}
									onClearGenres={() => setSelectedGenres([])}
									variant="upcoming"
									skeletonCount={initialLimit}
								/>
							</Suspense>
						)}
					</TabsContent>

					<TabsContent value="past" className="mt-0">
						{selectedTab === "past" ? (
							<ShowTabContent
								isInitialLoad={isInitialLoad}
								showsLoading={showsLoading}
								data={data}
								currentShows={currentShows}
								facepileData={facepileData}
								isFacepileLoading={isFacepileLoading}
								isFetchingNextPage={isFetchingNextPage}
								hasActiveFilters={hasActiveFilters}
								searchTerm={search.value}
								selectedCity={selectedCity}
								selectedDateLabel={dateFilterLabel}
								selectedVenue={selectedVenue}
								selectedGenres={selectedGenres}
								onClearSearch={() => search.setValue("")}
								onClearCity={() => setSelectedCity("all")}
								onClearDate={() => setDateFilter({ preset: null, startDate: null, endDate: null })}
								onClearVenue={() => setSelectedVenue("all")}
								onClearGenres={() => setSelectedGenres([])}
								variant="past"
								skeletonCount={initialLimit}
							/>
						) : (
							<Suspense fallback={<TabLoadingSkeleton />}>
								<LazyShowTabContent
									isInitialLoad={isInitialLoad}
									showsLoading={showsLoading}
									data={data}
									currentShows={currentShows}
									facepileData={facepileData}
									isFacepileLoading={isFacepileLoading}
									isFetchingNextPage={isFetchingNextPage}
									hasActiveFilters={hasActiveFilters}
									searchTerm={search.value}
									selectedCity={selectedCity}
									selectedDateLabel={dateFilterLabel}
									selectedVenue={selectedVenue}
									selectedGenres={selectedGenres}
									onClearSearch={() => search.setValue("")}
									onClearCity={() => setSelectedCity("all")}
									onClearDate={() => setDateFilter({ preset: null, startDate: null, endDate: null })}
									onClearVenue={() => setSelectedVenue("all")}
									onClearGenres={() => setSelectedGenres([])}
									variant="past"
									skeletonCount={initialLimit}
								/>
							</Suspense>
						)}
					</TabsContent>

					<TabsContent value="all" className="mt-0">
						{selectedTab === "all" ? (
							<ShowTabContent
								isInitialLoad={isInitialLoad}
								showsLoading={showsLoading}
								data={data}
								currentShows={currentShows}
								facepileData={facepileData}
								isFacepileLoading={isFacepileLoading}
								isFetchingNextPage={isFetchingNextPage}
								hasActiveFilters={hasActiveFilters}
								searchTerm={search.value}
								selectedCity={selectedCity}
								selectedDateLabel={dateFilterLabel}
								selectedVenue={selectedVenue}
								selectedGenres={selectedGenres}
								onClearSearch={() => search.setValue("")}
								onClearCity={() => setSelectedCity("all")}
								onClearDate={() => setDateFilter({ preset: null, startDate: null, endDate: null })}
								onClearVenue={() => setSelectedVenue("all")}
								onClearGenres={() => setSelectedGenres([])}
								variant="all"
								skeletonCount={initialLimit}
							/>
						) : (
							<Suspense fallback={<TabLoadingSkeleton />}>
								<LazyShowTabContent
									isInitialLoad={isInitialLoad}
									showsLoading={showsLoading}
									data={data}
									currentShows={currentShows}
									facepileData={facepileData}
									isFacepileLoading={isFacepileLoading}
									isFetchingNextPage={isFetchingNextPage}
									hasActiveFilters={hasActiveFilters}
									searchTerm={search.value}
									selectedCity={selectedCity}
									selectedDateLabel={dateFilterLabel}
									selectedVenue={selectedVenue}
									selectedGenres={selectedGenres}
									onClearSearch={() => search.setValue("")}
									onClearCity={() => setSelectedCity("all")}
									onClearDate={() => setDateFilter({ preset: null, startDate: null, endDate: null })}
									onClearVenue={() => setSelectedVenue("all")}
									onClearGenres={() => setSelectedGenres([])}
									variant="all"
									skeletonCount={initialLimit}
								/>
							</Suspense>
						)}
					</TabsContent>
				</Tabs>
			</div>
		</>
	);
}
